// WHO PAID A PAID EXPENSE — one partner, or both with each one's part (migration 069).
//
// Pure helpers behind the payer field both dialogs share (components/PayerSplitField),
// and behind the payer cell on every expense list. No figure is computed here: the server
// refuses any split whose parts do not add up to the amount, and the gap credits each
// partner by their part. What this does is keep the form honest before it is sent — Save
// stays dark until the parts add up — and print a split row the same way everywhere.
//
// IN AGOROT. 333.33 + 666.67 must equal 1,000.00 exactly, not a float near it, because
// the server compares in agorot and a form that disagreed would let a Save through only
// to be refused.
import { formatMoney } from '../components/Money';

export const BOTH = 'both';

const toCents = (v) => Math.round(Number(v) * 100);
const fromCents = (c) => (c / 100).toFixed(2).replace(/\.00$/, '');

// The field's starting state from a stored row: its one payer, or BOTH with each part.
// A new row (or an unpaid one) starts on nobody chosen, which the field shows as the first
// partner — the same fallback the single select always had.
export const initialPayerState = (expense) => {
  const payers = (expense && expense.payers) || [];
  if (payers.length > 1) {
    return { who: BOTH, parts: Object.fromEntries(payers.map((p) => [p.partner_id, String(p.amount)])) };
  }
  return { who: payers.length ? String(payers[0].partner_id) : '', parts: {} };
};

// Half each, the odd agora to the first partner — so the two always add up exactly.
export const halves = (amount, partners) => {
  const total = toCents(amount);
  if (!Number.isFinite(total) || partners.length !== 2) return {};
  const second = Math.floor(total / 2);
  return { [partners[0].id]: fromCents(total - second), [partners[1].id]: fromCents(second) };
};

// Typing one partner's part fills the other's with what is left, when there are two.
export const withRemainder = (parts, partnerId, value, amount, partners) => {
  const next = { ...parts, [partnerId]: value };
  const other = partners.length === 2 ? partners.find((p) => String(p.id) !== String(partnerId)) : null;
  const total = toCents(amount);
  if (other && value !== '' && Number.isFinite(toCents(value)) && Number.isFinite(total)) {
    next[other.id] = fromCents(total - toCents(value));
  }
  return next;
};

// Does the state describe a valid split of `amount`? One payer always does — they pay
// the whole of it. Both: every part above zero, and the parts adding up in agorot.
export const splitAddsUp = (state, amount, partners) => {
  if (state.who !== BOTH) return true;
  const cents = partners.map((p) => toCents(state.parts[p.id]));
  if (cents.some((c) => !Number.isFinite(c) || c <= 0)) return false;
  return cents.reduce((s, c) => s + c, 0) === toCents(amount);
};

// What the server is sent: `payers` for both, `paid_by_partner_id` for one — the
// server's own shorthand for one payer of the whole amount.
export const payersPayload = (state, partners) => {
  if (state.who === BOTH) {
    return { payers: partners.map((p) => ({ partner_id: p.id, amount: Number(state.parts[p.id]) })) };
  }
  const id = Number(state.who) || (partners[0] && partners[0].id);
  return { paid_by_partner_id: id };
};

// One payer → their name. Both → each name with its part, so the row says how it split.
export const payersLine = (payers) => {
  if (!payers || !payers.length) return '—';
  if (payers.length === 1) return payers[0].name || '—';
  return payers.map((p) => `${p.name} ${formatMoney(p.amount)}`).join(' · ');
};
